// Admin Authentication API Client
// Based on Angular: /FrontEnd/src/app/services/admin-auth.service.ts

import { apiClient } from '@/core/api/axios-config';
import type {
    AdminLoginRequest,
    AdminLoginResponse,
    AdminUser,
    UpdateProfileRequest,
    ChangePasswordRequest,
    ProfileImageResponse,
} from '../types/admin-auth.types';

const ADMIN_AUTH_BASE = '/admin/auth';

export const adminAuthApi = {
    /**
     * Admin login
     */
    login: async (credentials: AdminLoginRequest): Promise<AdminLoginResponse> => {
        const response = await apiClient.post<AdminLoginResponse>(
            `${ADMIN_AUTH_BASE}/login`,
            credentials
        );
        return response.data;
    },

    /**
     * Admin logout
     */
    logout: async (): Promise<void> => {
        await apiClient.post(`${ADMIN_AUTH_BASE}/logout`, {});
    },

    /**
     * Get current admin info
     */
    getCurrentAdmin: async (): Promise<AdminUser> => {
        const response = await apiClient.get<AdminUser>(`${ADMIN_AUTH_BASE}/me`);
        return response.data;
    },

    /**
     * Refresh token
     */
    refreshToken: async (refreshToken: string): Promise<AdminLoginResponse> => {
        const response = await apiClient.post<AdminLoginResponse>(
            `${ADMIN_AUTH_BASE}/refresh-token`,
            { refreshToken }
        );
        return response.data;
    },

    /**
     * Log admin activity
     */
    logActivity: async (
        action: string,
        entityType?: string,
        entityId?: string,
        details?: any
    ): Promise<void> => {
        await apiClient.post(`${ADMIN_AUTH_BASE}/log-activity`, {
            action,
            entityType,
            entityId,
            details,
        });
    },

    /**
     * Update profile
     */
    updateProfile: async (data: UpdateProfileRequest): Promise<{ message: string }> => {
        const response = await apiClient.put<{ message: string }>(`${ADMIN_AUTH_BASE}/profile`, data);
        return response.data;
    },

    /**
     * Change password
     */
    changePassword: async (data: ChangePasswordRequest): Promise<{ message: string }> => {
        const response = await apiClient.post<{ message: string }>(`${ADMIN_AUTH_BASE}/change-password`, data);
        return response.data;
    },

    /**
     * Upload profile image
     */
    uploadProfileImage: async (formData: FormData): Promise<ProfileImageResponse> => {
        const response = await apiClient.post<ProfileImageResponse>(`${ADMIN_AUTH_BASE}/profile-image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    /**
     * Remove profile image
     */
    removeProfileImage: async (): Promise<{ message: string }> => {
        const response = await apiClient.delete<{ message: string }>(`${ADMIN_AUTH_BASE}/profile-image`);
        return response.data;
    },
};
